<template>
  <div
      v-if="resolving || valueToDisplay > 0"
      class="countdown"
      :class="stateClass"
      role="timer"
      :aria-label="ariaLabel"
  >
    <svg class="countdown__dial" viewBox="0 0 120 120" aria-hidden="true">
      <circle class="countdown__track" cx="60" cy="60" :r="RADIUS"/>
      <!-- Drawn from twelve o'clock and drained clockwise. The dash offset is
           the whole mechanism: the ring is one stroked circle whose visible
           length is the fraction of the phase still to run. -->
      <circle
          class="countdown__sweep"
          cx="60"
          cy="60"
          :r="RADIUS"
          :stroke-dasharray="CIRCUMFERENCE"
          :stroke-dashoffset="dashOffset"
      />
    </svg>

    <span class="countdown__face">
      <template v-if="resolving">
        <span class="countdown__word">{{ $t('fightHud.resolving') }}</span>
      </template>
      <template v-else>
        <span class="countdown__value">{{ valueToDisplay }}</span>
        <span class="countdown__unit">{{ $t('fightHud.secondsShort') }}</span>
      </template>
    </span>
  </div>
</template>

<script>
const TICK_MS = 250;
const RADIUS = 54;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

export default {
  name: "CountDown",
  props: {
    deadline: {
      type: [String, Number, Date],
      required: true,
    },
    /**
     * When the phase opened. The ring needs a span, not just an end — without
     * it the dial has no full mark and falls back to the largest reading it
     * has seen, which is the remaining time at mount.
     */
    startedAt: {
      type: [String, Number, Date],
      default: null,
    },
    skewMs: {
      type: Number,
      default: 0,
    },
    /** Everyone has acknowledged and the server has yet to open the next phase. */
    resolving: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['expired'],
  setup() {
    return {RADIUS, CIRCUMFERENCE};
  },
  data() {
    return {
      valueToDisplay: 0,
      observedSpanSeconds: 0,
      countdownInterval: null,
      hasEmittedExpired: false,
    };
  },
  computed: {
    stateClass() {
      if (this.resolving) return 'is-resolving';
      if (this.valueToDisplay <= 5) return 'is-alert';
      if (this.valueToDisplay <= 15) return 'is-warning';
      return '';
    },
    deadlineMs() {
      return new Date(this.deadline).getTime();
    },
    spanSeconds() {
      if (this.startedAt) {
        const span = (this.deadlineMs - new Date(this.startedAt).getTime()) / 1000;
        if (span > 0) return span;
      }
      return this.observedSpanSeconds || 1;
    },
    fractionLeft() {
      if (this.resolving) return 1;
      return Math.min(Math.max(this.valueToDisplay / this.spanSeconds, 0), 1);
    },
    dashOffset() {
      return CIRCUMFERENCE * (1 - this.fractionLeft);
    },
    ariaLabel() {
      if (this.resolving) return this.$t('fightHud.resolving');
      return this.$t('fightHud.timerRemaining', {seconds: this.valueToDisplay});
    },
  },
  watch: {
    deadline() {
      this.startCountdown();
    },
  },
  methods: {
    correctedNow() {
      return Date.now() + this.skewMs;
    },
    secondsLeft() {
      return (this.deadlineMs - this.correctedNow()) / 1000;
    },
    updateCountdown() {
      const remaining = Math.max(Math.round(this.secondsLeft()), 0);
      this.valueToDisplay = remaining;
      if (remaining > this.observedSpanSeconds) {
        this.observedSpanSeconds = remaining;
      }
      if (remaining <= 0) {
        this.stopCountdown();
        if (!this.hasEmittedExpired) {
          this.hasEmittedExpired = true;
          this.$emit('expired');
        }
      }
    },
    startCountdown() {
      this.stopCountdown();
      this.hasEmittedExpired = false;
      this.observedSpanSeconds = 0;
      this.updateCountdown();
      if (this.valueToDisplay > 0) {
        this.countdownInterval = setInterval(this.updateCountdown, TICK_MS);
      }
    },
    stopCountdown() {
      if (this.countdownInterval) {
        clearInterval(this.countdownInterval);
        this.countdownInterval = null;
      }
    },
  },
  mounted() {
    this.startCountdown();
  },
  beforeUnmount() {
    this.stopCountdown();
  },
};
</script>

<style scoped>
.countdown {
  --dial: var(--cyber-cyan, #00ffff);
  position: relative;
  width: 5.5rem;
  height: 5.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--dial);
}

.countdown__dial {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  /* Twelve o'clock, clockwise. An SVG circle starts at three. */
  transform: rotate(-90deg);
}

.countdown__track {
  fill: rgba(8, 10, 14, 0.72);
  stroke: rgba(255, 255, 255, 0.12);
  stroke-width: 4;
}

.countdown__sweep {
  fill: none;
  stroke: currentColor;
  stroke-width: 4;
  stroke-linecap: round;
  filter: drop-shadow(0 0 0.35rem currentColor);
  /* Only the arc eases. A transition on the colour too made the last five
     seconds fade in slowly, which is precisely when it must read instantly. */
  transition: stroke-dashoffset 0.25s linear;
}

.countdown__face {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  line-height: 1;
  font-family: 'Orbitron', sans-serif;
}

.countdown__value {
  font-size: 1.75rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  text-shadow: 0 0 0.75rem currentColor;
}

.countdown__unit {
  margin-top: 0.15rem;
  font-size: 0.5rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  opacity: 0.55;
}

.countdown__word {
  font-size: 0.55rem;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  opacity: 0.75;
  text-align: center;
}

.countdown.is-warning {
  --dial: var(--cyber-yellow, #ffd147);
}

.countdown.is-alert {
  --dial: var(--crimson-red, #ff4d5e);
  animation: countdown-pulse 1s ease-in-out infinite;
}

.countdown.is-resolving {
  --dial: rgba(255, 255, 255, 0.55);
}

.countdown.is-resolving .countdown__dial {
  animation: countdown-spin 2.4s linear infinite;
}

@keyframes countdown-pulse {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.06);
  }
}

@keyframes countdown-spin {
  from {
    transform: rotate(-90deg);
  }
  to {
    transform: rotate(270deg);
  }
}

@media (prefers-reduced-motion: reduce) {
  .countdown.is-alert,
  .countdown.is-resolving .countdown__dial {
    animation: none;
  }
}
</style>
